import type { StepKind, TestStep } from '@1qa/shared';

/** Pure state machine for the no-code test builder (unit tested). */

export interface BuilderState {
  steps: TestStep[];
  dirty: boolean;
}

export type BuilderAction =
  | { type: 'load'; steps: TestStep[] }
  | { type: 'add'; kind: StepKind; value?: string; index?: number }
  | { type: 'update'; id: string; patch: Partial<Omit<TestStep, 'id'>> }
  | { type: 'remove'; id: string }
  | { type: 'duplicate'; id: string }
  | { type: 'move'; id: string; direction: -1 | 1 }
  | { type: 'reorder'; from: number; to: number }
  | { type: 'saved' };

let counter = 0;
export function newStepId(): string {
  counter += 1;
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID().slice(0, 8) : Math.random().toString(36).slice(2, 10);
  return `s_${rand}${counter}`;
}

export const DEFAULT_VALUES: Record<StepKind, string> = {
  open: '/',
  act: '',
  assert: '',
  wait: '1000',
};

export const initialBuilderState = (steps: TestStep[] = []): BuilderState => ({ steps, dirty: false });

function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= list.length) return list;
  const target = Math.max(0, Math.min(list.length - 1, to));
  const next = list.slice();
  const [item] = next.splice(from, 1);
  if (item === undefined) return list;
  next.splice(target, 0, item);
  return next;
}

export function builderReducer(state: BuilderState, action: BuilderAction): BuilderState {
  switch (action.type) {
    case 'load':
      return { steps: action.steps.map((s) => ({ ...s, id: s.id || newStepId() })), dirty: false };
    case 'add': {
      const step: TestStep = { id: newStepId(), kind: action.kind, value: action.value ?? DEFAULT_VALUES[action.kind] };
      const steps = state.steps.slice();
      const at = action.index === undefined ? steps.length : Math.max(0, Math.min(steps.length, action.index));
      steps.splice(at, 0, step);
      return { steps, dirty: true };
    }
    case 'update': {
      let changed = false;
      const steps = state.steps.map((s) => {
        if (s.id !== action.id) return s;
        changed = true;
        const next = { ...s, ...action.patch };
        // Switching to "wait" needs a numeric value; switching away from it clears the number.
        if (action.patch.kind && action.patch.kind !== s.kind && action.patch.value === undefined) {
          if (action.patch.kind === 'wait' && !/^\d+$/.test(s.value)) next.value = DEFAULT_VALUES.wait;
          else if (s.kind === 'wait') next.value = DEFAULT_VALUES[action.patch.kind];
        }
        return next;
      });
      return changed ? { steps, dirty: true } : state;
    }
    case 'remove': {
      const steps = state.steps.filter((s) => s.id !== action.id);
      return steps.length === state.steps.length ? state : { steps, dirty: true };
    }
    case 'duplicate': {
      const i = state.steps.findIndex((s) => s.id === action.id);
      const src = state.steps[i];
      if (!src) return state;
      const steps = state.steps.slice();
      steps.splice(i + 1, 0, { ...src, id: newStepId() });
      return { steps, dirty: true };
    }
    case 'move': {
      const i = state.steps.findIndex((s) => s.id === action.id);
      const j = i + action.direction;
      if (i < 0 || j < 0 || j >= state.steps.length) return state;
      return { steps: moveItem(state.steps, i, j), dirty: true };
    }
    case 'reorder': {
      const steps = moveItem(state.steps, action.from, action.to);
      return steps === state.steps ? state : { steps, dirty: true };
    }
    case 'saved':
      return { ...state, dirty: false };
  }
}

/** Validation shown inline in the builder; mirrors the API's zod rules plus friendly checks. */
export function stepProblems(step: TestStep): string | null {
  const v = step.value.trim();
  switch (step.kind) {
    case 'open':
      return v ? null : 'Enter the page to open, e.g. /login';
    case 'act':
      return v ? null : 'Describe what to do, e.g. click the "Sign in" button';
    case 'assert':
      return v ? null : 'Describe what should be true, e.g. the cart shows 2 items';
    case 'wait': {
      const n = Number(v);
      if (!/^\d+$/.test(v)) return 'Use a number of milliseconds, e.g. 1000';
      return n > 30000 ? 'Waits are limited to 30 seconds (30000 ms)' : null;
    }
  }
}

export function scenarioProblems(steps: TestStep[]): string[] {
  const problems: string[] = [];
  if (steps.length === 0) problems.push('Add at least one step.');
  if (steps.length > 200) problems.push('A test can have at most 200 steps.');
  steps.forEach((s, i) => {
    const p = stepProblems(s);
    if (p) problems.push(`Step ${i + 1}: ${p}`);
  });
  return problems;
}
